import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SpeechLanguageCode } from '@/app/features/chat/constants/speechLanguages';

/**
 * Speech-to-text UI state (spec: chatbot-speech-to-text).
 *
 * - `language`: the selected SPOKEN recognition language. Initialized from the
 *   user's display language and any persisted per-user override by the chat
 *   wiring (not here, to keep this slice free of side effects). Persisted via
 *   the existing chat-preference path.
 * - `disclosureAcknowledged`: whether the one-time "audio leaves the device"
 *   notice has been accepted in this browser. Hydrated from localStorage by the
 *   mic flow; gates the first capture.
 */
export interface SpeechState {
  /** Whether the active tenant has enabled the speech-to-text feature. */
  featureEnabled: boolean;
  language: SpeechLanguageCode;
  disclosureAcknowledged: boolean;
}

const initialState: SpeechState = {
  featureEnabled: false,
  language: 'english',
  disclosureAcknowledged: false,
};

const speechSlice = createSlice({
  name: 'speech',
  initialState,
  reducers: {
    /** Set by the tenant feature-flag fetch; gates all voice UI. */
    setSpeechFeatureEnabled(state, action: PayloadAction<boolean>) {
      state.featureEnabled = action.payload;
    },
    setSpeechLanguage(state, action: PayloadAction<SpeechLanguageCode>) {
      state.language = action.payload;
    },
    acknowledgeDisclosure(state) {
      state.disclosureAcknowledged = true;
    },
  },
});

export const { setSpeechFeatureEnabled, setSpeechLanguage, acknowledgeDisclosure } =
  speechSlice.actions;

export default speechSlice.reducer;

export const selectIsSpeechToTextEnabled = (state: { speech: SpeechState }) =>
  state.speech.featureEnabled;
export const selectSpeechLanguage = (state: { speech: SpeechState }) => state.speech.language;
export const selectSpeechDisclosureAcknowledged = (state: { speech: SpeechState }) =>
  state.speech.disclosureAcknowledged;
